import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Images, Loader2, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import {
  useFillDefaultPhoto,
  useGetProductDefaults,
  useUpdateProductDefaults,
} from '../../hooks/backend/settingsPage'
import { usePublishLabelProducts, useUploadProductPhoto } from '../../hooks/backend/inventoryPage'
import { publishLabels } from '../../utils/publishLabels'
import { HelpText } from '../../components/HelpText'

interface Defaults {
  photo_url: string | null
  small_price: number
  large_price: number
  table_missing?: boolean
  missing_photo_flavors: string[]
}

interface Draft {
  photo_url: string | null
  small_price: string
  large_price: string
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

const toDraft = (d: Defaults): Draft => ({
  photo_url: d.photo_url,
  small_price: String(d.small_price),
  large_price: String(d.large_price),
})

export default function ProductDefaultsSettings() {
  const getDefaults = useGetProductDefaults()
  const updateDefaults = useUpdateProductDefaults()
  const fillDefaultPhoto = useFillDefaultPhoto()
  const uploadPhoto = useUploadProductPhoto()
  const publishLabelProducts = usePublishLabelProducts()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const data = getDefaults.data as Defaults | null | undefined

  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [filling, setFilling] = useState(false)

  useEffect(() => {
    void getDefaults.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) setDraft(toDraft(data))
  }, [data])

  const patch = (p: Partial<Draft>) => setDraft(d => (d ? { ...d, ...p } : d))

  const upload = async (file: File | undefined) => {
    if (!file) return
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Use a PNG, JPG or WebP photo')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('The photo is too large (20 MB at most)')
      return
    }
    try {
      const res = (await uploadPhoto.trigger({
        fileName: `default-${file.name}`,
        data: await fileToBase64(file),
        mimeType: file.type,
      }).result) as { url?: string } | undefined
      if (!res?.url) throw new Error('No URL came back')
      patch({ photo_url: res.url })
      toast.success('Photo uploaded. Click Save to make it the default.')
    } catch (err) {
      toast.error(`Upload failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const save = async () => {
    if (!draft) return
    const small = Number(draft.small_price)
    const large = Number(draft.large_price)
    if (draft.small_price.trim() === '' || !Number.isFinite(small) || small < 0) {
      toast.error('Small price must be a number, 0 or more')
      return
    }
    if (draft.large_price.trim() === '' || !Number.isFinite(large) || large < 0) {
      toast.error('Large price must be a number, 0 or more')
      return
    }
    try {
      setSaving(true)
      await updateDefaults.trigger({ photo_url: draft.photo_url, small_price: small, large_price: large })
      await getDefaults.trigger({}, { skipCache: true })
      toast.success('New product defaults saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save the defaults')
    } finally {
      setSaving(false)
    }
  }

  const fill = async () => {
    try {
      setFilling(true)
      const res = (await fillDefaultPhoto.trigger({})) as { updated?: number; label_names?: string[] } | undefined
      const labels = res?.label_names ?? []
      await getDefaults.trigger({}, { skipCache: true })
      if (labels.length === 0) {
        toast.success('Every product already has a photo')
        return
      }
      toast.success(
        `Added a photo to ${res?.updated ?? labels.length} product${res?.updated === 1 ? '' : 's'} in the Hub. Publishing now…`,
      )
      await publishLabels(labels, p => publishLabelProducts.trigger(p))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add the photo')
    } finally {
      setFilling(false)
    }
  }

  const saved = data?.photo_url ?? null
  const dirty =
    !!draft &&
    !!data &&
    (draft.photo_url !== data.photo_url ||
      Number(draft.small_price) !== data.small_price ||
      Number(draft.large_price) !== data.large_price)
  const missing = data?.missing_photo_flavors ?? []

  return (
    <Card id="product-defaults">
      <CardHeader>
        <CardTitle>New Product Defaults</CardTitle>
        <CardDescription>
          <HelpText>
            What a new product starts with when you click Add Product. The default photo stands in
            until you upload a real one (adding a photo replaces it), and the price is filled in from
            the size. You can still change both before saving.
          </HelpText>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {getDefaults.error && !data ? (
          <p className="text-sm text-destructive">Couldn&apos;t load the defaults.</p>
        ) : !draft || !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-5">
            {data.table_missing && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                Run the 3.111.0 database update before saving these.
              </p>
            )}

            <div className="space-y-1.5">
              <Label>Default photo</Label>
              <div className="flex flex-wrap items-start gap-3">
                {draft.photo_url ? (
                  <a
                    href={draft.photo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-28 w-28 shrink-0 overflow-hidden rounded-md border bg-muted"
                    title="Open the default photo"
                  >
                    <img src={draft.photo_url} alt="Default product" className="h-full w-full object-cover" />
                  </a>
                ) : (
                  <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                    None
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={e => void upload(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={uploadPhoto.loading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadPhoto.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    {uploadPhoto.loading ? 'Uploading…' : draft.photo_url ? 'Replace photo' : 'Upload photo'}
                  </Button>
                  {draft.photo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground hover:text-destructive"
                      onClick={() => patch({ photo_url: null })}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
              <div className="space-y-1.5">
                <Label htmlFor="pd-small-price">Small price ($)</Label>
                <Input
                  id="pd-small-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.small_price}
                  onChange={e => patch({ small_price: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pd-large-price">Large price ($)</Label>
                <Input
                  id="pd-large-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.large_price}
                  onChange={e => patch({ large_price: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={() => void save()} disabled={saving || !dirty} className="gap-1.5">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>

            <div className="space-y-2 rounded-md border bg-muted/40 p-3">
              <p className="text-sm font-medium">Products without a photo</p>
              {missing.length === 0 ? (
                <p className="text-sm text-muted-foreground">Every product has a photo.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {missing.length} flavor{missing.length === 1 ? '' : 's'}: {missing.join(', ')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <HelpText>
                      A size whose other size has photos gets those; the rest get the default photo.
                      Then each flavor is published to its Publish To destinations.
                    </HelpText>
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={filling || !saved || dirty}
                    onClick={() => void fill()}
                  >
                    {filling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />}
                    Add the default photo to {missing.length} flavor{missing.length === 1 ? '' : 's'}
                  </Button>
                  {(!saved || dirty) && (
                    <p className="text-xs text-muted-foreground">Save a default photo first.</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
